/**
 * Preferences + unit-scoped availability fetch for the merged table
 * (offer-unified-table, CU-86cbc3r8b) — the "fetch" half of
 * `use-offer-availability-data.ts`'s data seam; see that file's own doc
 * comment for why this is split out.
 *
 * Always fetches and renders the offer's own full `defaultDateFrom`..
 * `defaultDateTo` campaign range — no movable/extendable window (CU-86cbejhgd
 * removed it as a regression; the merged table never had a legitimate reason
 * to show less than the offer's own range, unlike the catalog's genuinely
 * unbounded inventory).
 */
import * as React from 'react';
import { useOfferUnitAvailability } from '../api/use-offer-unit-availability';
import { useOfferTableAvailabilityPreferences } from './use-offer-table-availability-preferences';
import { useOfferReservationCheck } from './use-offer-reservation-check';
import { useOfferAvailabilityPeriods } from './use-offer-availability-periods';
import type { OfferUnitItem } from '../units-tab-columns';

/** Pure helper — extracted so `useOfferAvailabilityQuery` stays under the max-lines-per-function budget. */
function buildMediaUnitIdById(rows: OfferUnitItem[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) map.set(String(row.id), row.mediaUnitId);
  return map;
}

export interface UseOfferAvailabilityQueryOptions {
  offerId: number;
  rows: OfferUnitItem[];
  defaultDateFrom: string | null;
  defaultDateTo: string | null;
  forceShow: boolean;
}

export function useOfferAvailabilityQuery({
  offerId,
  rows,
  defaultDateFrom,
  defaultDateTo,
  forceShow,
}: UseOfferAvailabilityQueryOptions) {
  const preferences = useOfferTableAvailabilityPreferences();
  const effectiveShowAvailability = forceShow || preferences.showAvailability;

  const rowIds = React.useMemo(() => rows.map((r) => Number(r.id)), [rows]);
  const mediaUnitIdById = React.useMemo(() => buildMediaUnitIdById(rows), [rows]);

  const hasCampaignDates = defaultDateFrom !== null && defaultDateTo !== null;

  // No `from`/`to` — omitting them returns the offer's own full, unpaginated
  // period range (the endpoint's backward-compatible default), matching the
  // retired separate "Dostępność" tab's pre-merge behavior instead of an
  // arbitrary bounded window.
  const availabilityQuery = useOfferUnitAvailability(offerId, {
    unitIds: effectiveShowAvailability && hasCampaignDates ? rowIds : [],
  });

  // Deliberately independent of `effectiveShowAvailability` (the rendered
  // columns' show/hide toggle) so the bulk-remove reservation-aware warning
  // stays correct regardless of whether the availability columns are
  // currently shown (bug-detection-sweep fix).
  const reservationCheckQuery = useOfferReservationCheck({
    offerId,
    rowIds,
    hasCampaignDates,
    defaultDateFrom,
    defaultDateTo,
  });

  const periods = useOfferAvailabilityPeriods({
    effectiveShowAvailability,
    defaultDateFrom,
    defaultDateTo,
    granularity: preferences.granularity,
  });

  return {
    preferences,
    effectiveShowAvailability,
    hasCampaignDates,
    availabilityQuery,
    reservationCheckQuery,
    periods,
    rowIds,
    mediaUnitIdById,
  };
}
